import React, { useEffect, useState } from "react";

import { mdiPlayCircleOutline } from "@mdi/js";
import { Icon } from "@mdi/react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Button from "../button/Button";
import Select from "../form/Select";
import PlayerModal from "../playerModal/PlayerModal";

import type { JSX } from "react";

import type { Broadcast } from "../../api/FastAPI.types";
import type { GetMediaResponse, MediaUpdated } from "../../api/MediaAPI.types";

interface NewURL extends URL {
  // This is to overcome the fact that URL.canParse is not implemented in our version of TS
  // And we are stuck with this version of TS...
  canParse: (url: string) => boolean;
}

interface ContentInternalSourceProps {
  content: GetMediaResponse;
  onItemUpdate: (content: MediaUpdated) => void;
  broadcastList: Broadcast[];
  isLoading: boolean;
}

interface FormValues {
  broadcast: {
    value: string;
    label?: string;
    url?: string;
  };
}

export default function InternalSourceSection({
  content,
  onItemUpdate,
  broadcastList,
  isLoading,
}: ContentInternalSourceProps): JSX.Element {
  const methods = useForm<FormValues>({
    values: {
      broadcast: {
        value: content.fast_source?.fast_channel_broadcast_uuid ?? "",
      },
    },
  });
  const { t } = useTranslation("editor-content");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const selectedBroadcast = useWatch({
    control: methods.control,
    name: "broadcast",
  });

  useEffect(() => {
    if (
      !selectedBroadcast ||
      selectedBroadcast.value ===
        content.fast_source?.fast_channel_broadcast_uuid
    )
      return;

    onItemUpdate({
      ...content,
      fast_source: {
        broadcast_url: selectedBroadcast.url,
        fast_channel_broadcast_uuid: selectedBroadcast.value,
      },
    });
  }, [selectedBroadcast, content, onItemUpdate, methods]);

  const optionList = broadcastList.map((broadcast) => ({
    label: `${broadcast.fast_channel_name} - ${broadcast.name}`,
    url: broadcast.broadcast_url,
    value: broadcast.uuid,
  }));

  const broadcastURL = content.fast_source?.broadcast_url;

  const canParseContentURL =
    broadcastURL && (URL as unknown as NewURL).canParse(broadcastURL);

  const isShowVideoDisabled =
    !broadcastURL || !canParseContentURL || content.status === "archived";

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...methods}>
      <div className="max-w-125">
        <Select
          isCompact
          isLoading={isLoading}
          label="Chaine FAST"
          name="broadcast"
          options={optionList}
        />
      </div>
      <div className="flex">
        <Button
          isDisabled={isShowVideoDisabled}
          onClick={(): void => setIsPreviewOpen(true)}
          size="xs"
          type="button"
        >
          <Icon path={mdiPlayCircleOutline} size={0.7} />
          {t("showVideo")}
        </Button>
      </div>
      <PlayerModal
        isPlaying
        useSimplePlayer
        content={content.fast_source?.broadcast_url ?? ""}
        isOpen={isPreviewOpen}
        onClose={(): void => setIsPreviewOpen(false)}
      />
    </FormProvider>
  );
}
